"use client";

import { useEffect, useRef } from "react";

import log from "logbeacon";
import { useShellRouter } from "@/src/hooks";
import { useSnapshot } from "valtio";
import { ROUTER_PATH } from "../config";
import { isProtectedRoute } from "../config/protectedRoutes";
import { rootStore } from "../store";
import { usePathname } from "@hooks/usePathname";

/**
 * Hook to protect routes that require authentication
 * Should be used in root layout component
 *
 * @example
 * ```tsx
 * export function RootLayoutClient({ children }) {
 *   useRouteProtection();
 *   return <>{children}</>;
 * }
 * ```
 */
export const useRouteProtection = () => {
  const router = useShellRouter();
  const pathname = usePathname(true);
  const { isLoading, isLoggedIn, isFetching } = useSnapshot(rootStore.login);
  const redirectedRef = useRef(false);

  useEffect(() => {
    // Reset redirect flag when pathname changes
    redirectedRef.current = false;
  }, [pathname]);

  useEffect(() => {
    // Skip if not a protected route
    if (!isProtectedRoute(pathname)) {
      return;
    }

    // Prevent multiple redirects
    if (redirectedRef.current) {
      return;
    }

    // Wait for login state to be determined
    if (isLoading || isFetching) {
      return;
    }

    // Redirect to home if not logged in
    // 排除邀请页面：/aura/[handle] 和 /task/invite/[inviteCode]
    const isInvitePage =
      pathname.includes("/aura/") || pathname.includes("/task/invite/");
    if (!isLoggedIn && !isInvitePage) {
      redirectedRef.current = true;
      log.info(
        "[useRouteProtection] User not logged in, redirecting from",
        pathname,
        "to home",
      );
      router.replace(ROUTER_PATH.HOME);
      return;
    }
  }, [isLoading, isLoggedIn, pathname, router, isFetching]);
};
